import { Card, Table, Button, Form, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaSyncAlt,
  FaUserCheck,
  FaUserTimes,
  FaChartLine,
} from "react-icons/fa";

function CustomerTable({
  customers,
  search,
  setSearch,
  changeStatus,
  renewCustomer,
  startEditCustomer,
  deleteCustomer,
  daysLeft,
}) {
  const navigate = useNavigate();

  const filteredCustomers = customers.filter((customer) => {
    const searchText = search.toLowerCase();

    return (
      customer.name?.toLowerCase().includes(searchText) ||
      customer.email?.toLowerCase().includes(searchText)
    );
  });

  const getAccessText = (callAccess) => {
    if (!callAccess || callAccess.length === 0) {
      return "No Access";
    }

    return callAccess.join(" + ");
  };

  const getDaysBadge = (customer) => {
    const remainingDays = daysLeft(customer.expiryDate);

    if (customer.status !== "Active" || remainingDays <= 0) {
      return <Badge bg="danger">Expired</Badge>;
    }

    if (remainingDays <= 7) {
      return (
        <Badge bg="warning" text="dark">
          {remainingDays} Days Left
        </Badge>
      );
    }

    return <Badge bg="success">{remainingDays} Days Left</Badge>;
  };

  const openCustomerTrades = (customer) => {
    navigate(`/customer-trades/${customer.id}`);
  };

  return (
    <Card className="shadow border-0">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div>
            <h4 className="fw-bold mb-1">Customer List</h4>

            <p className="text-muted mb-0">
              View and manage all registered customers.
            </p>
          </div>

          <div style={{ minWidth: "280px" }}>
            <div className="d-flex align-items-center">
              <FaSearch className="me-2 text-muted" />

              <Form.Control
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <Table hover bordered className="align-middle mb-0">
            <thead className="table-dark">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Call Access</th>
                <th>Status</th>
                <th>Expiry Date</th>
                <th>Days Left</th>
                <th style={{ minWidth: "340px" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No customers found.
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <Button
                        variant="link"
                        className="p-0 fw-bold text-decoration-none"
                        onClick={() => openCustomerTrades(customer)}
                      >
                        {customer.name}
                      </Button>
                    </td>

                    <td>{customer.email}</td>

                    <td>{customer.plan}</td>

                    <td>
                      <Badge bg="info" text="dark">
                        {getAccessText(customer.callAccess)}
                      </Badge>
                    </td>

                    <td>
                      {customer.status === "Active" ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="danger">Expired</Badge>
                      )}
                    </td>

                    <td>{customer.expiryDate || "-"}</td>

                    <td>{getDaysBadge(customer)}</td>

                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="dark"
                          onClick={() => openCustomerTrades(customer)}
                        >
                          <FaChartLine className="me-1" />
                          View Trades
                        </Button>

                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => startEditCustomer(customer)}
                        >
                          <FaEdit className="me-1" />
                          Edit
                        </Button>

                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => renewCustomer(customer)}
                        >
                          <FaSyncAlt className="me-1" />
                          Renew
                        </Button>

                        <Button
                          size="sm"
                          variant={
                            customer.status === "Active"
                              ? "warning"
                              : "secondary"
                          }
                          onClick={() =>
                            changeStatus(customer.id, customer.status)
                          }
                        >
                          {customer.status === "Active" ? (
                            <>
                              <FaUserTimes className="me-1" />
                              Expire
                            </>
                          ) : (
                            <>
                              <FaUserCheck className="me-1" />
                              Activate
                            </>
                          )}
                        </Button>

                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => deleteCustomer(customer.id)}
                        >
                          <FaTrash className="me-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <p className="text-muted small mt-3 mb-0">
          Click customer name or View Trades to see customer-wise invested calls.
        </p>
      </Card.Body>
    </Card>
  );
}

export default CustomerTable;